/**
 * DPR Form Types and Interfaces
 * Location: frontend/gcp-user-portal/src/types/dpr.types.ts
 */

/**
 * Main DPR Form Data Interface
 * Covers Parts A, B, and C
 */
export interface DPRFormData {
  // ============= PART A: GENERAL INFORMATION =============

  // Land Parcel Details
  landParcelId?: string;
  landParcelName?: string;
  projectName?: string;
  landArea?: number;
  areaUnit?: "ha" | "m2" | "acre";
  projectType?: ProjectType;
  circle?: string;
  acknowledgementDate?: string;

  // Land Location
  state?: string;
  district?: string;
  division?: string;
  range?: string;
  block?: string;
  village?: string;

  // GCA Details
  gcaId?: string;
  gcaName?: string;
  gcaType?: GCAType;
  gcaLegalStatus?: string;
  gcaRegistrationNumber?: string;
  registrationNumber?: string;
  gcaAddress?: string;
  gcaEmail?: string;
  gcaPhone?: string;

  // Contact Person Details
  contactPerson?: string;
  contactPersonDesignation?: string;
  contactPersonAddress?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactCountryCode?: string;

  // Nodal Officers
  stateNodalOfficerId?: string;
  stateNodalOfficerName?: string;
  stateNodalOfficerDesignation?: string;
  stateNodalOfficerContact?: string;
  districtNodalOfficerId?: string;
  districtNodalOfficerName?: string;
  districtNodalOfficerDesignation?: string;
  districtNodalOfficerContact?: string;

  // DPR Purpose (Multi-select)
  dprPurpose?: DPRPurpose[];

  // Green Credits
  estimatedGreenCredits?: number;
  greenCreditUsageProjects?: string;

  // Timestamps
  submissionDate?: Date;

  // ============= PART B: SITE CHARACTERISTICS =============

  // 1. General Site Description
  landUseCategory?: string;
  landLegalStatus?: string;
  topography?: Topography;
  slopePercentage?: number;
  nearestRoadType?: string;
  distanceFromRoad?: number;
  distanceFromHabitation?: number;
  approachRoute?: string;
  climaticZone?: string;
  inspectionPaths?: string;

  // 2. Soil Characteristics
  soilType?: SoilType;
  soilDepthTexture?: string;
  soilMoistureRegime?: SoilMoistureRegime;
  soilPH?: number;
  soilFertilityStatus?: string;

  // 3. Water Availability
  waterSource?: WaterSource;
  waterSupplyLines?: string;
  distanceToNearestWaterSource?: number;
  suitabilityForConservation?: string;

  // 4. Existing Vegetation
  vegetationType?: VegetationType;
  dominantSpecies?: string;
  canopyDensity?: number;
  treesGirth0to10?: number;
  treesGirth10to30?: number;
  treesGirth30to60?: number;
  treesGirthGreater60?: number;
  invasiveSpeciesPresent?: string;
  invasiveSpeciesDetails?: string;

  // 5. Existing Fauna
  existingFauna?: string;

  // 6. Socio-Environmental Context
  proximityToLocalCommunities?: number;
  nearbySttlements?: string;
  rightsAndUsage?: string;
  communityDependency?: string;
  existingRestrictions?: string;

  // ============= PART C: INTERVENTIONS & ZONING =============

  // Zoning & Activity Mapping
  kmlFileId?: string;
  kmlFileName?: string;

  // Intervention Type & Areas
  interventionType?: InterventionType;

  // Tree Plantation & Assisted Natural Regeneration
  treePlantationArea?: number;
  treePlantationSpecies?: string;
  seedlingsPerHectare?: number;
  spacingPattern?: string;
  nurserySource?: NurserySource;
  nurseryLocation?: string;
  temporaryNurseryDetails?: string;
  maintenancePlan?: string;
  treeSpecies?: TreeSpecies[];

  // Soil & Moisture Conservation Works
  soilConservationArea?: number;
  conservationWorkType?: string;
  conservationWorkZone?: string;
  conservationWorkPurpose?: string;

  // Water Supply & Irrigation Support
  waterSupplyArea?: number;
  proposedWaterSource?: string;
  supplyLineLayout?: string;
  waterHarvestingStructures?: string;

  // Protection Measures
  protectionArea?: number;
  fencingType?: FencingType;
  fencingLength?: number;
  firePreventionWorks?: string;
  watchWardArrangements?: string;

  // Biodiversity and Ecosystem Enrichment
  biodiversityArea?: number;
  pollinatorSpecies?: string;
  ntfpSpecies?: string;
  grassesShrubsMedicinal?: string;

  // Implementation Timeline - Year-wise Targets (Year 1-10)
  // Each year contains multiple targets with indicator, activity, unit, numberOfUnits, costRequired
  yearwiseTargetsData1?: TargetEntry[];
  yearwiseTargetsData2?: TargetEntry[];
  yearwiseTargetsData3?: TargetEntry[];
  yearwiseTargetsData4?: TargetEntry[];
  yearwiseTargetsData5?: TargetEntry[];
  yearwiseTargetsData6?: TargetEntry[];
  yearwiseTargetsData7?: TargetEntry[];
  yearwiseTargetsData8?: TargetEntry[];
  yearwiseTargetsData9?: TargetEntry[];
  yearwiseTargetsData10?: TargetEntry[];

  // ============= PART D: DECLARATIONS AND UNDERTAKINGS =============

  // Declaration by the GCA - Checkboxes
  declarationTrueComplete?: boolean;
  declarationAbideRules?: boolean;
  declarationAdequateResources?: boolean;
  declarationMaintenanceFund?: boolean;
  declarationPerformanceParameters?: boolean;
  declarationProgressReports?: boolean;
  declarationNoLandClaim?: boolean;
  declarationNoCommercialActivity?: boolean;

  // Status & Workflow
  status?: DPRStatus;
  draftSavedAt?: Date;
  submittedAt?: Date;
  approvedAt?: Date;
}

/**
 * Enumerations and Types
 */

export enum ProjectType {
  TREE_PLANTATION = "TREE_PLANTATION",
  WATER_CONSERVATION = "WATER_CONSERVATION",
  LAND_LEASED = "LAND_LEASED",
}

export enum GCAType {
  INDIVIDUAL = "INDIVIDUAL",
  GOVT_INSTITUTION = "GOVT_INSTITUTION",
  PSU = "PSU",
  NGO = "NGO",
}

export enum DPRPurpose {
  COMPENSATORY_AFFORESTATION = "COMPENSATORY_AFFORESTATION",
  CSR = "CSR",
  ESG = "ESG",
  TREE_PLANTATION_COMPLIANCE = "TREE_PLANTATION_COMPLIANCE",
}

export enum Topography {
  PLAIN = "PLAIN",
  UNDULATING = "UNDULATING",
  HILLY = "HILLY",
}

export enum SoilType {
  SANDY = "SANDY",
  CLAYEY = "CLAYEY",
  LOAMY = "LOAMY",
  LATERITIC = "LATERITIC",
}

export enum SoilMoistureRegime {
  DRY = "DRY",
  MOIST = "MOIST",
  SEASONAL_WATERLOGGED = "SEASONAL_WATERLOGGED",
}

export enum WaterSource {
  NATURAL_STREAMS = "NATURAL_STREAMS",
  HAND_PUMPS = "HAND_PUMPS",
  BORE_WELLS = "BORE_WELLS",
  TANKS = "TANKS",
}

export enum VegetationType {
  SCRUB = "SCRUB",
  GRASSLAND = "GRASSLAND",
  REGENERATING_FOREST = "REGENERATING_FOREST",
  DEGRADED_PLANTATION = "DEGRADED_PLANTATION",
}

export enum InterventionType {
  TREE_PLANTATION = "TREE_PLANTATION",
  SOIL_MOISTURE = "SOIL_MOISTURE",
  WATER_SUPPLY = "WATER_SUPPLY",
  PROTECTION = "PROTECTION",
  BIODIVERSITY = "BIODIVERSITY",
}

export enum NurserySource {
  GOVERNMENT = "GOVERNMENT",
  PRIVATE = "PRIVATE",
  TEMPORARY = "TEMPORARY",
}

export enum FencingType {
  BARBED_WIRE = "BARBED_WIRE",
  STONE_WALL = "STONE_WALL",
  WOODEN = "WOODEN",
  MIXED = "MIXED",
}

export enum DPRStatus {
  DRAFT = "DRAFT",
  SUBMITTED = "SUBMITTED",
  UNDER_REVIEW = "UNDER_REVIEW",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

/**
 * Supporting Interfaces
 */

export interface TreeSpecies {
  id: string;
  botanicalName: string;
  commonName?: string;
  seedlings: number; // per hectare
  spacing: string; // e.g., "2x2"
  nurseryDetails?: string;
}

export interface InterventionZone {
  id: string;
  name: string;
  type: InterventionType;
  areaHectares: number;
  geometry?: any; // GeoJSON geometry - any type for flexibility
  description?: string;
}

/**
 * Target Entry - matches backend TargetEntity structure
 * Each target belongs to a year, has an indicator, activity, unit, numberOfUnits, and cost
 */
export interface TargetEntry {
  id?: string;
  indicator: IndicatorType | "";
  activity: string;
  unit: string;
  numberOfUnits: number | undefined;
  costPerUnit: number | undefined;
  costRequired: number | undefined;
  description?: string;
}

/**
 * @deprecated Use TargetEntry instead - kept for backward compatibility
 */
export interface YearwiseActivity {
  id?: string;
  activity: string;
  milestoneDeliverable: string;
}

/**
 * Indicator types matching backend ENUM_INDICATORS
 */
export enum IndicatorType {
  FENCING = "Fencing",
  PROCURING_SEEDLINGS = "Procuring Seedlings",
  NURSERY_FOR_SEEDLINGS = "Nursery for Seedlings",
  LAND_SITE_PREPARATION_WORKS = "Land Preparation Works",
  ECO_RESTORATION_WORKS = "Eco Restoration Works",
  PLANTING_OPERATIONS = "Planting Operations",
  IRRIGATION_MEASURES = "Irrigation Measures",
  PROTECTION = "Protection",
  OTHER_NECESSARY_PROVISIONS = "Other Necessary Provisions",
}

/**
 * Activity options per indicator - matches backend activity enums
 */
export const INDICATOR_ACTIVITIES: Record<IndicatorType, string[]> = {
  [IndicatorType.FENCING]: [
    "Chain link fencing",
    "Barbed wire fencing",
    "Stone wall fencing",
    "Trench fencing",
    "Pucca wall construction",
    "Thorn fencing",
    "Loose stone fencing",
    "New ditch fencing",
    "Others (if any)",
  ],
  [IndicatorType.PROCURING_SEEDLINGS]: ["Purchase of seedlings"],
  [IndicatorType.NURSERY_FOR_SEEDLINGS]: [
    "Preparation of Nursery Beds",
    "Purchase of Seed",
    "Purchase of Polybags/Polypots",
    "Seed Sowing",
    "Fertilizer/Manure Application",
    "Irrigation to Seedling",
    "Conversion to tall plants",
    "Others (if any)",
  ],
  [IndicatorType.LAND_SITE_PREPARATION_WORKS]: [
    "Preparation of maps",
    "Placing of sign board",
    "Clearing of unwanted growth",
    "Soil analysis",
    "Soil improvement(addition of manure, fertilizers e.t.c.)",
    "Staking",
    "Pitting",
    "Others (if any)",
  ],
  [IndicatorType.ECO_RESTORATION_WORKS]: [
    "Continuous Contour Trenching(CCT)",
    "Staggered Contour Trenching",
    "Deep CCT",
    "Water Harvesting Structures",
    "Loose Boulder Structure",
    "Gabion Structures",
    "Grass lands",
    "Planting of herbs/shrubs",
    "Water bodies restoration",
    "Others (if any)",
  ],
  [IndicatorType.PLANTING_OPERATIONS]: [
    "Number of seedlings planted",
    "Transportation of Seedlings",
    "Weeding",
    "Treatment of pits",
    "Casualty Replacement",
    "Others (if any)",
  ],
  [IndicatorType.IRRIGATION_MEASURES]: [
    "Bore well / Tube wells",
    "Others (if any)",
  ],
  [IndicatorType.PROTECTION]: [
    "Engaging manpower for watch and ward",
    "Fire Line",
    "Van kutir construction",
    "Others (if any)",
  ],
  [IndicatorType.OTHER_NECESSARY_PROVISIONS]: [
    "Contingency",
    "Monitoring",
    "Others (if any)",
  ],
};

/**
 * Target Year enum matching backend ENUM_TARGET_YEAR
 */
export enum TargetYear {
  FirstYear = "1st Year",
  SecondYear = "2nd Year",
  ThirdYear = "3rd Year",
  FourthYear = "4th Year",
  FifthYear = "5th Year",
  SixthYear = "6th Year",
  SeventhYear = "7th Year",
  EighthYear = "8th Year",
  NinthYear = "9th Year",
  TenthYear = "10th Year",
}

export interface DPRSubmissionPayload extends DPRFormData {
  projectDetailId: string;
  userId: string;
  submittedAt: Date;
}

export interface DPRDraftPayload extends DPRFormData {
  projectDetailId: string;
  userId: string;
  savedAt: Date;
}

/**
 * Form Validation Errors
 */

export type DPRFormErrors = Partial<Record<keyof DPRFormData, string>>;

export interface ValidationResult {
  isValid: boolean;
  errors: DPRFormErrors;
}

/**
 * API Response Types
 */

export interface DPRSubmissionResponse {
  success: boolean;
  message: string;
  data: {
    dprId: string;
    projectDetailId: string;
    status: DPRStatus;
    submittedAt: Date;
  };
}

export interface DPRDraftResponse {
  success: boolean;
  message: string;
  data: {
    dprId: string;
    projectDetailId: string;
    status: DPRStatus;
    savedAt: Date;
  };
}

/**
 * Component Props Types
 */

export interface DPRFormProps {
  projectDetailId?: string;
  initialData?: Partial<DPRFormData>;
  onSubmit?: (data: DPRFormData) => Promise<void>;
  onSaveDraft?: (data: DPRFormData) => Promise<void>;
  isLoading?: boolean;
  isSubmitting?: boolean;
}

export interface TreeSpeciesDialogProps {
  open: boolean;
  editingTree: TreeSpecies | null;
  onClose: () => void;
  onSave: (tree: TreeSpecies) => void;
  isLoading?: boolean;
}

/**
 * Form Step Configuration
 */

export interface FormStep {
  id: string;
  label: string;
  description?: string;
  sections: string[];
  requiredFields: string[];
}

export const DPR_FORM_STEPS: FormStep[] = [
  {
    id: "executive-summary",
    label: "Executive Summary",
    description: "Overview of the project",
    sections: [],
    requiredFields: [],
  },
  {
    id: "part-a",
    label: "Part A - General Information",
    description: "Land parcel and GCA details",
    sections: ["landParcelDetails", "gcaDetails", "dprPurpose"],
    requiredFields: [
      "projectName",
      "landArea",
      "projectType",
      "gcaName",
      "gcaType",
      "contactPerson",
      "contactEmail",
      "contactPhone",
      "dprPurpose",
    ],
  },
  {
    id: "part-b",
    label: "Part B - Site Characteristics",
    description: "Baseline and site information",
    sections: [
      "generalSiteDescription",
      "soilCharacteristics",
      "waterAvailability",
      "existingVegetation",
      "socioEnvironmentalContext",
    ],
    requiredFields: [
      "topography",
      "soilType",
      "soilDepth",
      "vegetationType",
      "canopyDensity",
    ],
  },
  {
    id: "part-c",
    label: "Part C - Interventions & Zoning",
    description: "Proposed interventions and zoning",
    sections: [
      "zoningOfActivities",
      "treeIntervention",
      "protectionMeasures",
      "biodiversityEnrichment",
      "implementationTimeline",
    ],
    requiredFields: [
      "interventionType",
      "treeSpecies",
      "spacingPattern",
      "implementationTimeline",
    ],
  },
];

/**
 * Master Data Types
 */

export interface TreeSpeciesMaster {
  id: string;
  botanicalName: string;
  commonName: string;
  region?: string;
  growthRate?: "SLOW" | "MEDIUM" | "FAST";
  waterRequirement?: "LOW" | "MEDIUM" | "HIGH";
  soilPreference?: string;
}

export interface DivisionMaster {
  id: string;
  name: string;
  state: string;
  circle?: string;
}

export interface NodalOfficerMaster {
  id: string;
  name: string;
  designation: string;
  email: string;
  phone: string;
  state: string;
  district?: string;
  level: "STATE" | "DISTRICT";
}
